import React from "react";
import { FilterState } from "@/components/FilterSidebar/FilterState";
import { Skeleton } from "@/components/ui";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip";
import { FilterName } from "@/components/FilterSidebar/types";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useFilterFieldOptions } from "../../FilterSidebar/hooks/useFilterFieldsOptions";
import { getPillValueAsText } from "./util";

const MAX_VALUE_COUNT = 2; // max number of filter values to show in each pill
const MAX_FILTER_PILL_COUNT = 8;

export const FilterPillsInner = ({
  filterState,
  onFilterRemove,
}: {
  filterState?: FilterState;
  onFilterRemove?: (name: FilterName) => void;
}) => {
  const { t } = useTranslation();
  const activeFitlers = Object.entries(filterState?.getActiveFilters() || {});
  const displayedFilters = activeFitlers.slice(0, MAX_FILTER_PILL_COUNT);
  const hiddenFilters = activeFitlers.slice(MAX_FILTER_PILL_COUNT);
  const { options, loading } = useFilterFieldOptions();

  if (loading) {
    return (
      <>
        {displayedFilters.map(([name]) => (
          <Skeleton key={name} className="h-5.5 w-30 rounded-xl" />
        ))}
        {activeFitlers.length > MAX_FILTER_PILL_COUNT && (
          <Skeleton className="h-5.5 w-20 rounded-xl" />
        )}
      </>
    );
  }

  if (displayedFilters.length === 0) return <NoFiltersSelectedPill />;

  return (
    <>
      {filterState &&
        options &&
        displayedFilters.map((activeFitler) => {
          const [name, value] = activeFitler;
          return (
            // <div key={name} className="">
            //   <MiniFilterPill
            //     name={name as FilterName}
            //     onRemove={onFilterRemove}
            //   >
            //     {getPillValueAsText(
            //       name as FilterName,
            //       value,
            //       options,
            //       t,
            //       MAX_VALUE_COUNT,
            //     )}
            //   </MiniFilterPill>
            // </div>
            <Tooltip key={name}>
              <TooltipTrigger asChild>
                <div>
                  <MiniFilterPill
                    name={name as FilterName}
                    onRemove={onFilterRemove}
                  >
                    {getPillValueAsText(
                      name as FilterName,
                      value,
                      options,
                      t,
                      MAX_VALUE_COUNT,
                    )}
                  </MiniFilterPill>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {getPillValueAsText(name as FilterName, value, options, t)}
              </TooltipContent>
            </Tooltip>
          );
        })}

      {hiddenFilters.length > 0 && (
        <Tooltip>
          <TooltipTrigger>
            <PlusMorePill count={hiddenFilters.length} />
          </TooltipTrigger>
          {options && (
            <TooltipContent>
              {hiddenFilters.map((hiddenFilter) => {
                const [name, value] = hiddenFilter;
                return (
                  <div key={name}>
                    {getPillValueAsText(name as FilterName, value, options, t)}
                  </div>
                );
              })}
            </TooltipContent>
          )}
        </Tooltip>
      )}
    </>
  );
};

const MiniFilterPill = ({
  name,
  onRemove,
  children,
}: {
  name: FilterName;
  onRemove?: (name: FilterName) => void;
  children?: React.ReactNode;
}) => {
  return (
    <div className="flex cursor-pointer items-center gap-1 rounded-xl border px-2 text-xs leading-5 text-foreground-medium hover:border-border-dark">
      {children}
      <button
        className="h-3.5 w-3.5"
        onClick={() => onRemove && onRemove(name)}
      >
        <X className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
    </div>
  );
};

const PlusMorePill = ({ count }: { count: number }) => {
  const { t } = useTranslation();
  return (
    <div className="flex cursor-pointer items-center gap-1 rounded-xl border px-2 text-xs leading-5 text-foreground-medium">
      {t("filters.pills.plusMore", { plusCount: `+${count}` })}
    </div>
  );
};

const NoFiltersSelectedPill = () => {
  const { t } = useTranslation();
  return (
    <div className="flex h-5.5 cursor-pointer items-center gap-1 rounded-xl border border-dashed px-2 text-xs leading-5 text-foreground-medium">
      {t("filters.pills.noFiltersSelected")}
    </div>
  );
};
